import {
  GatewayControleQuinquennal,
  GatewayInfoSocieteOrias,
  GatewayTicketControleQuinquennal,
} from "@conformite/gateway";
import {
  getControleQuinquennalDateButoir,
  TICKET_STATUS_MAP,
  TicketStatus,
} from "@src/helper/ticket.helper";

export type CQOriasRadiee = {
  id: string;
  idEntreprise: string;
  siren: string;
  entrepriseName: string;
  millesime: number;
  status: GatewayControleQuinquennal.ControleQuinquennalStatus;
  pipelineStage: GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus;
  dateDebut: string;
  dateDepot: string | null;
  dateFin: string | null;
  dateButoir: string | null;
  conformiteIa: number | null;
  conformiteVerificateur: number | null;
  avancementTraite: number;
  avancementTotal: number;
  estMonControle: boolean;
};

/**
 * Ligne de liste complétée de ses métriques : la liste ne les porte plus, elles
 * arrivent par le lot `metriques` — SIREN compris, sans lequel rien ne peut
 * être croisé avec l'ORIAS.
 */
export type CQListRow =
  GatewayControleQuinquennal.ControleQuinquennalAdminListResponse &
    Partial<GatewayControleQuinquennal.ControleQuinquennalMetriquesAvecSiren>;

const STAGES_TERMINES: GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus[] =
  [TicketStatus.FERME, TicketStatus.RADIE, TicketStatus.ANNULE];

/**
 * Portée du scan ORIAS : un contrôle déjà terminé, annulé ou radié n'a plus à
 * être remonté, et l'exclure réduit d'autant les sirens envoyés à l'ORIAS.
 */
const TOUS_LES_STAGES = Object.keys(
  TICKET_STATUS_MAP
) as GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus[];

export const STAGES_SCAN_ORIAS = TOUS_LES_STAGES.filter(
  (stage) => !STAGES_TERMINES.includes(stage)
);

export const collectSirens = (rows: CQListRow[]) => [
  ...new Set(
    rows
      .map(({ siren }) => siren)
      .filter((siren): siren is string => Boolean(siren))
  ),
];

const toIso = (date: Date | string | null | undefined) =>
  date == null ? null : new Date(date).toISOString();

export const toCQOriasRadiee = (
  row: CQListRow,
  siren: string
): CQOriasRadiee => ({
  id: row.id,
  idEntreprise: row.idEntreprise,
  siren,
  entrepriseName: row.entrepriseName,
  millesime: row.millesime,
  status: row.status,
  pipelineStage: row.pipelineStage,
  dateDebut: new Date(row.dateDebut ?? row.dateCreation).toISOString(),
  dateDepot: toIso(row.dateDepot),
  dateFin: toIso(row.dateFin),
  dateButoir: toIso(getControleQuinquennalDateButoir(row)),
  conformiteIa: row.conformiteIa ?? null,
  conformiteVerificateur: row.conformiteVerificateur ?? null,
  avancementTraite: row.questionsRepondues ?? 0,
  avancementTotal: row.questionsTotal ?? 0,
  estMonControle: row.estMonControle,
});

/**
 * Le statut porté par le contrôle est celui que la collecte hebdomadaire a
 * persisté ; le cache ne prend le relais que sur un contrôle jamais collecté.
 */
const isRadiee = (
  row: CQListRow,
  siren: string,
  bySiren: Record<string, GatewayInfoSocieteOrias.InfoSocieteOriasType>
) =>
  row.oriasStatut
    ? row.oriasStatut === GatewayControleQuinquennal.OriasStatut.SUPPRIME
    : bySiren[siren]?.statut === GatewayInfoSocieteOrias.StatutSociete.SUPPRIME;

export const filterOriasRadiees = (
  rows: CQListRow[],
  bySiren: Record<string, GatewayInfoSocieteOrias.InfoSocieteOriasType>
) =>
  rows.flatMap((row) => {
    const { siren } = row;
    // Un ticket HubSpot désynchronisé laisse `pipelineStage` vide : toutes les
    // vues de ce tableau indexent TICKET_STATUS_MAP sans garde.
    if (!siren || !TICKET_STATUS_MAP[row.pipelineStage]) return [];
    return isRadiee(row, siren, bySiren) ? [toCQOriasRadiee(row, siren)] : [];
  });

export type CQOriasRadieesCriteres = {
  pipelineStages: string[];
  millesimes?: number[];
  dateDebutFrom?: string;
  dateDebutTo?: string;
  dateDepotFrom?: string;
  dateDepotTo?: string;
  dateFinFrom?: string;
  dateFinTo?: string;
  search?: string;
  mesControles?: boolean;
};

const dansIntervalle = (valeur: string | null, from?: string, to?: string) => {
  if (!from && !to) return true;
  if (!valeur) return false;
  const instant = new Date(valeur).getTime();
  if (from && instant < new Date(from).getTime()) return false;
  if (to && instant > new Date(to).getTime()) return false;
  return true;
};

/**
 * Restreint l'encart ORIAS aux mêmes critères que la liste principale : la
 * liste est filtrée côté serveur, l'encart repose sur un scan complet en cache
 * et doit donc rejouer ces filtres côté client. Les bornes de dates reprennent
 * la sémantique de la gateway (`>=`/`<=` sur la date brute), la recherche celle
 * du `entrepriseName LIKE` insensible à la casse.
 */
export const filtrerOriasRadieesAffichees = (
  rows: CQOriasRadiee[],
  criteres: CQOriasRadieesCriteres
): CQOriasRadiee[] => {
  const recherche = criteres.search?.trim().toLowerCase() ?? "";
  return rows.filter((row) => {
    if (criteres.mesControles && !row.estMonControle) return false;
    if (
      criteres.pipelineStages.length > 0 &&
      !criteres.pipelineStages.includes(row.pipelineStage)
    )
      return false;
    if (
      criteres.millesimes &&
      criteres.millesimes.length > 0 &&
      !criteres.millesimes.includes(row.millesime)
    )
      return false;
    if (
      !dansIntervalle(
        row.dateDebut,
        criteres.dateDebutFrom,
        criteres.dateDebutTo
      )
    )
      return false;
    if (
      !dansIntervalle(
        row.dateDepot,
        criteres.dateDepotFrom,
        criteres.dateDepotTo
      )
    )
      return false;
    if (!dansIntervalle(row.dateFin, criteres.dateFinFrom, criteres.dateFinTo))
      return false;
    if (recherche && !row.entrepriseName.toLowerCase().includes(recherche))
      return false;
    return true;
  });
};
